import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useRaynetConnection } from '@/hooks/useRaynet';
import { raynetClient } from '@/api/raynetClient';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

export function ConfigurationTab() {
  const { data: isConnected, refetch: testConnection, isLoading: isTestingConnection } = useRaynetConnection();
  
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleTestConnection = async () => {
    setTestResult(null);
    
    try {
      const result = await testConnection();
      
      if (result.data) {
        setTestResult({
          success: true,
          message: 'Připojení k Raynet API je funkční'
        });
      } else {
        setTestResult({
          success: false,
          message: 'Nepodařilo se připojit k Raynet API. Zkontrolujte prosím přihlašovací údaje.'
        });
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Chyba při testování připojení: ' + (error instanceof Error ? error.message : 'Neznámá chyba')
      });
    }
  };

  const isConfigured = raynetClient.isConfigured();
  const currentEnv = {
    apiKey: import.meta.env.VITE_RAYNET_API_KEY,
    instance: import.meta.env.VITE_RAYNET_INSTANCE,
    user: import.meta.env.VITE_RAYNET_USER,
  };

  return (
    <div className="space-y-6">
      {/* Raynet Connection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {isConnected ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-600" />
            )}
            Raynet API připojení
          </CardTitle>
          <CardDescription>
            Konfigurace a testování připojení k Raynet CRM systému
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>API Klíč</Label>
              <Input
                value={currentEnv.apiKey ? '••••••••' + currentEnv.apiKey.slice(-4) : 'Není nastaven'}
                disabled
                className="bg-muted"
              />
            </div>
            <div>
              <Label>Instance</Label>
              <Input
                value={currentEnv.instance || 'Není nastavena'}
                disabled
                className="bg-muted"
              />
            </div>
            <div>
              <Label>Uživatel</Label>
              <Input
                value={currentEnv.user || 'Není nastaven'}
                disabled
                className="bg-muted"
              />
            </div>
          </div>

          {/* Test Connection */}
          <div className="flex gap-2">
            <Button
              onClick={handleTestConnection}
              disabled={isTestingConnection || !isConfigured}
              className="flex items-center gap-2"
            >
              {isTestingConnection ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4" />
              )}
              Test připojení
            </Button>
          </div>

          {/* Test Result */}
          {testResult && (
            <Alert className={testResult.success ? 'border-green-200 bg-green-50' : ''} variant={testResult.success ? 'default' : 'destructive'}>
              {testResult.success ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <AlertDescription className={testResult.success ? 'text-green-800' : ''}>
                {testResult.message}
              </AlertDescription>
            </Alert>
          )}

          {!isConfigured && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Raynet API není nakonfigurován. Nastavte prosím environment variables:
                VITE_RAYNET_API_KEY, VITE_RAYNET_INSTANCE, VITE_RAYNET_USER
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 